/**
 * T.Z49 — the UseDesk support widget: the decisions, framework-free.
 *
 * T.Z116 (owner, 2026-09-17): the widget is mounted ONCE at the app root — above
 * the router — so it exists on every surface the SPA renders, the unauthenticated
 * ones included: a signed-out visitor on `/login` (both doors) or `/logged-out`
 * gets an ANONYMOUS widget (`usedeskIdentity(null)` → no `userIdentify` call at
 * all, the chat just arrives unidentified), and a resolved session identifies it
 * exactly as T.Z49 did. This module is the pure half of the feature: the flag
 * reading, the identity shaping and the mount policy. It is importable from the
 * plain-Node `unit` project, so — like every other `*-model.ts` — it touches no
 * `fetch`, no `document` and no `import.meta.env`; the React mount reads the flag
 * and hands it over (`./UsedeskWidget.tsx`), and the DOM adapter is
 * `./usedesk-bridge.ts`.
 *
 * The widget and the identity policy follow Linear ISM2-6304 and the UseDesk
 * widget API (`api.usedocs.com/article/51387`): `usedeskMessenger.userIdentify`
 * carries the visitor's name + e-mail into the ticket. OWNER, 2026-09-14: the
 * widget shows for ALL roles — ADMIN, МЕНЕДЖЕР, КУРАТОР and plain viewers — so
 * none of the functions below ever reads a role.
 */

/**
 * The tenant widget UseDesk issued for this campaign (the widget id is in the
 * path). T.Z126 swapped this to `widget_171027_75851.js`; T.Z128 (owner,
 * 2026-09-17) rolled the swap back — the tenant script is 66139 again.
 */
export const USEDESK_SCRIPT_URL =
  'https://s3.usedesk.ru/lib/secure.usedesk.ru/widget_171027_66139.js';

/**
 * The `id` of the injected `<script>` tag — what makes a second mount (a
 * StrictMode re-run, a re-login without a page reload) a no-op instead of a
 * second copy of the widget.
 */
export const USEDESK_SCRIPT_ID = 'cbm-usedesk-widget';

/**
 * The build flag baked by Vite at build time (see `vite-env.d.ts`, `Dockerfile`).
 * For reference only: the component must read it as the LITERAL
 * `import.meta.env.VITE_USEDESK_ENABLED` — Vite replaces that text, so a dynamic
 * `import.meta.env[USEDESK_FLAG_ENV]` would read `undefined` in every build.
 */
export const USEDESK_FLAG_ENV = 'VITE_USEDESK_ENABLED';

/** What the widget needs to name a ticket owner — exactly the `/auth/me` fields. */
export interface UsedeskIdentity {
  name: string;
  email: string;
}

/** A `/auth/me` user, structurally (`SessionUser` of `@cb-manager/shared`). */
export type UsedeskUserLike = { name?: string; email?: string } | null | undefined;

/**
 * The build flag, read as Vite bakes it (a string, absent when unset). Only the
 * exact spelling `'true'` (or the boolean `true`) turns the widget on: any build
 * that does not set the flag — local `pnpm dev`, the e2e `vite build`, a plain
 * `pnpm build` — ships without the third-party script at all. The images that
 * deploy (kind + prod) get `VITE_USEDESK_ENABLED=true` from the Dockerfile ARG
 * default, which is why "off in e2e, on in prod" needs no chart value.
 */
export function usedeskEnabled(raw: string | boolean | undefined | null): boolean {
  return raw === true || raw === 'true';
}

/**
 * A plausible e-mail: something, an `@`, something after it. The synthetic
 * identities carry none — `AUTH_DISABLED`'s `LOCAL_USER` is «local», the guard
 * fallback `UNKNOWN_USER` is «unknown» — so a dev / e2e contour never hands
 * UseDesk a made-up ticket owner.
 */
export function isPlausibleEmail(value: string): boolean {
  const at = value.indexOf('@');
  return at > 0 && at < value.length - 1;
}

/**
 * The identity we may hand to UseDesk, or `null` when we may not: both fields
 * must be non-empty after trimming and the e-mail must look like one. A `null`
 * here means "the chat still works, the ticket just arrives unidentified" —
 * the safe degradation ISM2-6304 pinned, never a broken widget.
 */
export function usedeskIdentity(user: UsedeskUserLike): UsedeskIdentity | null {
  const name = typeof user?.name === 'string' ? user.name.trim() : '';
  const email = typeof user?.email === 'string' ? user.email.trim() : '';
  if (name === '' || !isPlausibleEmail(email)) return null;
  return { name, email };
}

/**
 * True when the identity the widget should carry has moved — a first login,
 * an account switch, or a logout. Only a real change re-identifies: UseDesk
 * ignores `userIdentify` for a client it already knows, so re-sending the same
 * pair buys nothing.
 */
export function identityChanged(
  prev: UsedeskIdentity | null,
  next: UsedeskIdentity | null,
): boolean {
  if (prev === null || next === null) return prev !== next;
  return prev.name !== next.name || prev.email !== next.email;
}

/**
 * The mount policy in one pure function: the flag is on AND the session question
 * is ANSWERED.
 *
 * T.Z116 (owner, 2026-09-17) — `'anonymous'` is a YES now, not a no: the widget
 * lives at the app root (above the router), so it is on `/login` and
 * `/logged-out` too and boots there WITHOUT an identity (the `null` arm of
 * {@link usedeskIdentity}) — a visitor who cannot sign in is exactly the visitor
 * who needs support. `'loading'` stays a NO: the first paint of the widget is
 * either an anonymous one on a page that knows nobody is in, or an identified
 * one, never a guess that the identity hook has to take back.
 */
export function usedeskShouldMount(input: {
  enabled: boolean;
  status: 'loading' | 'ready' | 'anonymous';
}): boolean {
  return input.enabled && input.status !== 'loading';
}
